import api from './api';

export interface DashboardStats {
  clientes: { total: number; activos: number };
  contratos: { total: number; valorTotal: number };
  pagos: { 
    total: number; 
    totalRecaudado: number;
    ingresosMensuales?: number;
    ingresosAnuales?: number;
    pagosPendientes?: number;
  };
  propuestas: { total: number };
  ingresosMensuales?: number;
  ingresosAnuales?: number;
  pagosPendientes?: number;
}

export interface ChartData {
  labels: string[];
  data: number[];
}

export interface AnalisisFinanciero {
  resumen: {
    totalIngresos: number;
    totalGastos: number;
    utilidadNeta: number;
    margenUtilidad: number;
  };
  ingresosPorRubro: Array<{
    rubroId: number;
    rubroNombre: string;
    total: number;
  }>;
  gastosPorRubro: Array<{
    rubroId: number;
    rubroNombre: string;
    total: number;
  }>;
}

export interface AnalisisMensual {
  mes: number;
  ingresos: number;
  gastos: number;
  utilidad: number;
}

export interface AnalisisPresupuestalRubro {
  rubroId: number;
  rubroCodigo: string;
  rubroNombre: string;
  rubroTipo: 'ingreso' | 'gasto';
  totalPresupuestado: number;
  totalEjecutado: number;
  totalComprometido: number;
  totalProyectado: number;
  desviacion: number;
  porcentajeEjecucion: number;
  porcentajeProyectado: number;
  meses: Array<{
    mes: number;
    presupuestado: number;
    ejecutado: number;
  }>;
}

export interface AnalisisPresupuestalMensual {
  mes: number;
  presupuestoIngreso: number;
  presupuestoGasto: number;
  ingresoReal: number;
  ingresoComprometido: number;
  ingresoProyectado: number;
  gastoReal: number;
  gastoComprometido: number;
  gastoProyectado: number;
  desviacionIngreso: number;
  desviacionGasto: number;
  porcentajeEjecucionIngreso: number;
  porcentajeEjecucionGasto: number;
}

export interface RentabilidadConcepto {
  conceptoId: number;
  conceptoCodigo: string;
  conceptoNombre: string;
  rubroNombre: string;
  rubroTipo: 'ingreso' | 'gasto';
  totalIngresos: number;
  totalGastos: number;
  rentabilidad: number;
  margen: number;
}

class DashboardService {
  async getSummary(year?: number): Promise<DashboardStats> {
    const params = year ? `?year=${year}` : '';
    const response = await api.get<DashboardStats>(`/reports/summary${params}`);
    return response.data;
  }

  async getContratosPorMes(year?: number): Promise<ChartData> {
    const params = year ? `?year=${year}` : '';
    const response = await api.get<ChartData>(`/reports/contratos-por-mes${params}`);
    return response.data;
  }

  async getEstadoContratos(year?: number): Promise<ChartData> {
    const params = year ? `?year=${year}` : '';
    const response = await api.get<ChartData>(`/reports/estado-contratos${params}`);
    return response.data;
  }

  async getConceptosContratados(year?: number): Promise<any[]> {
    const params = year ? `?year=${year}` : '';
    const response = await api.get(`/reports/conceptos-contratados${params}`);
    return response.data;
  }

  async getAnalisisFinanciero(year: number, month?: number): Promise<AnalisisFinanciero> {
    const params = new URLSearchParams();
    params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    
    const response = await api.get(`/reports/analisis-financiero?${params.toString()}`);
    return response.data;
  }

  async getAnalisisMensual(year: number): Promise<AnalisisMensual[]> {
    const response = await api.get(`/reports/analisis-mensual?year=${year}`);
    return response.data;
  }

  async getAnalisisPresupuestal(year: number): Promise<AnalisisPresupuestalRubro[]> {
    const response = await api.get(`/reports/analisis-presupuestal?year=${year}`);
    return response.data;
  }

  async getAnalisisPresupuestalMensual(year: number): Promise<AnalisisPresupuestalMensual[]> {
    const response = await api.get(`/reports/analisis-presupuestal-mensual?year=${year}`);
    return response.data;
  }

  async getRentabilidadConceptos(year: number): Promise<RentabilidadConcepto[]> {
    const response = await api.get(`/reports/rentabilidad-conceptos?year=${year}`);
    return response.data;
  }

  async getExpiringContractsCount(): Promise<{ count: number }> {
    const response = await api.get('/reports/contratos-vencimiento-mes');
    return response.data;
  }

  async getExpiringPaymentsCount(): Promise<{ count: number }> {
    const response = await api.get('/reports/pagos-vencimiento-proximo');
    return response.data;
  }
}

const dashboardService = new DashboardService();
export default dashboardService;
